import { Form, Field, FormElement, FormRenderProps } from '@progress/kendo-react-form';
import { Checkbox, Input } from '@progress/kendo-react-inputs';
import { useState, useContext, useEffect } from 'react';

import { SetValueOnObject } from '../../utils/UnsafeTypescriptUtil';
import { Operator, blankOperator, apiModelToOperator } from '../../models/operator';
import { useParams } from 'react-router-dom';
import { protectedResources } from '../../authConfig';
import { useFetchWithMsal } from '../../hooks/useFetchWithMsal';
import { LoadPage } from '../../components/LoadPage';
import { StreckDropDownTypes, TypedStreckDropDown } from '../../components/TypedStreckDropDown';
import { OperatorContext, AppUserContext } from '../../components/PvsContextProvider';
import { useNotificationAPI } from '../../components/NotificationProvider';
import { emailValidator, inputLabel } from '../../components/Validators';

interface ManageOperatorProps {
    labId?: number,
    isPopup?: boolean,
    children?: React.ReactNode
}

const pageTitle = 'Operater Manager';

const ManageOperator = (props: ManageOperatorProps) => {
    const params = useParams();
    const context = useContext(OperatorContext);
    const appUserContext = useContext(AppUserContext);
    const { addNotification } = useNotificationAPI();

    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const [operator, setOperator] = useState(
        SetValueOnObject(structuredClone(blankOperator), "Id",
            (params.OperatorId === undefined ?
                0 :
                params.OperatorId)) as Operator);

    const onChange_Operator = (e: any, propName: string) => {
        let updatedOperator: Operator = SetValueOnObject(operator, propName, e.target.value) as Operator;
        setOperator(updatedOperator);
    }

    const [inactive, setInactive] = useState(false);

    const handleSubmit = (formRenderProps: FormRenderProps) => {
        if (!formRenderProps.valid) {
            return;
        }
        const apiEndpoint = protectedResources.apiOperator.endpoint +
            (operator.Id > 0 ?
                "UpdateOperator" :
                "CreateOperator");

        let date = undefined;
        if (inactive) {
            if (operator.InactiveDate) {
                date = operator.InactiveDate
            }
            else {
                date = new Date()
            }
        }

        execute(operator.Id > 0 ? 'PUT' : 'POST', apiEndpoint, {
            Id: operator.Id,
            OperatorName: operator.OperatorName,
            LabId: operator.LabId,
            Department: operator.Department,
            Title: operator.Title,
            Email: operator.Email,
            InactiveDate: date,
        })
            .then(
                ((result: any) => {
                    if (result?.id) {
                        addNotification('Operator ' + (operator.Id > 0 ? 'updated' : 'created') + ': ' + operator.OperatorName, pageTitle, 'success');
                        context.refresh();
                        if (operator.Id === 0 && !props.isPopup) {
                            //window.history.replaceState(null, '', '/operator-manage/' + result?.id);
                            setOperator(SetValueOnObject(structuredClone(blankOperator), "LabId", operator.LabId) as Operator);
                        } else {
                            setOperator(apiModelToOperator(result));
                        }
                    }
                })
            );
    };

    const getOperator = () => {
        if (operator.Id > 0) {
            execute("GET", protectedResources.apiOperator.endpoint + "GetOperator/?Id=" + operator.Id)
                .then(
                    ((result: any) => {
                        if (result) {
                            let model = apiModelToOperator(result);
                            setOperator(model);
                            setInactive(model.InactiveDate !== null && model.InactiveDate !== undefined);
                        }
                    })
                );
        } else {
            setOperator(SetValueOnObject(structuredClone(blankOperator), "LabId",
                (props.labId === undefined ?
                    0 :
                    props.labId)) as Operator);
        }
    }

    const handleLabChange = (labId: number) => {
        let updatedOperator: Operator = SetValueOnObject(operator, "LabId", labId) as Operator;
        setOperator(updatedOperator);
    }

    useEffect(() => {
        getOperator();
    }, [execute]);

    return <LoadPage isLoading={isLoading}>
        <Form initialValues={operator} key={JSON.stringify(operator)} render={(formRenderProps) =>
            <FormElement style={{ maxWidth: 650 }}>
                <fieldset className={'k-form-fieldset'}>
                    <legend className={'k-form-legend'}>Please fill in the fields:</legend>

                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'OperatorName'} component={Input} label={'Operator Name*'} required={true} onChange={(event) => onChange_Operator(event, "OperatorName")} />
                        </div>
                        {operator.Id > 0 &&
                            <Checkbox label={' :Inactive'} labelPlacement={"before"} checked={inactive} onChange={(event) => setInactive(event.value)} />}
                    </div>

                    <div className="row">
                        <div className="mb-3 col">
                            <TypedStreckDropDown
                                streckDropDownType={StreckDropDownTypes.Lab}
                                handleChange={(result: any) => handleLabChange(result)}
                                selectedId={operator.LabId}
                                required={true}
                                disabled={operator.Id > 0}
                            ></TypedStreckDropDown>
                        </div>
                    </div>

                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'Department'} component={Input} label={'Department'} required={false} onChange={(event) => onChange_Operator(event, "Department")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={'Title'} component={Input} label={'Title'} required={false} onChange={(event) => onChange_Operator(event, "Title")} />
                        </div>
                    </div>
                    <div className="row">
                        <div className="mb-3 col">
                            <Field name={"Email"} validator={emailValidator} component={inputLabel} label={"Email*"} required={false} onChange={(event) => onChange_Operator(event, "Email")} />
                        </div>
                    </div>

                </fieldset>
                <div className="k-form-buttons">
                    <button type={'submit'} onClick={() => handleSubmit(formRenderProps)} hidden={appUserContext.isReadOnly} className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base">
                        {operator.Id > 0 ? "Update" : "Create"}
                    </button>
                </div>
            </FormElement>} />
    </LoadPage>;
};

export default ManageOperator;